import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BottomNav from '../components/BottomNav';
import UserHeader from '../components/UserHeader';
import { computeAggregates, loadScores, Aggregates } from '../utils/progressStorage';
import { getStudentProgressSnapshot } from '../utils/parentStudentFirestore';
import { getVerseScoresByUser } from '../utils/verseScoresFirestore';
import { getParasha, getTargetDate } from '../utils/userSelections';
import { getParashaByEnglish } from '../utils/parashaNameMap';
import { trackPageView } from '../utils/analytics';
import tora from '../../data/torah.json';
import cheeringKid from '../assets/webp/cheering-kid.webp';

const BLUE = '#3B7BF4';
const GOLD = '#F5C842';
const TEXT_DARK = '#1A1A2E';
const TEXT_GRAY = '#6B7280';
const BORDER = '#E5E7EB';
const BG = '#F5F7FB';

// Sun=0→א … Sat=6→ש
const HEBREW_DAYS = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'];

const MyProgressPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language?.startsWith('he');
  const navigate = useNavigate();
  const [agg, setAgg] = useState<Aggregates | null>(null);
  const [userName, setUserName] = useState<string>('');
  const [userPhotoURL, setUserPhotoURL] = useState<string | null>(null);
  const [totalRecordings, setTotalRecordings] = useState(0);
  const [parashaUnique, setParashaUnique] = useState(0);
  const [parashaLastVerse, setParashaLastVerse] = useState<number | null>(null);
  const [distinctParashot, setDistinctParashot] = useState(0);

  const parasha = getParasha();
  const targetDate = getTargetDate();

  // Total verses in the selected parasha from torah.json
  const courseLimit = useMemo(() => {
    if (!parasha) return 0;
    const key = parasha.english.toLowerCase().replace("'", '');
    return (tora as { parasha?: string }[]).filter(
      (v) => v.parasha && v.parasha.toLowerCase().replace("'", '') === key,
    ).length;
  }, [parasha]);

  useEffect(() => {
    trackPageView('MyProgress');

    let email = '';
    let userId = '';
    try {
      const raw = localStorage.getItem('loggedInUser');
      if (raw) {
        const u = JSON.parse(raw) as {
          email?: string;
          displayName?: string;
          name?: string;
          userId?: string;
          uid?: string;
          photoURL?: string | null;
        };
        const name = u.displayName || u.name || '';
        if (name) setUserName(name);
        setUserPhotoURL(u.photoURL || null);
        email = u.email || '';
        userId = u.userId ?? u.uid ?? u.email ?? '';
      }
    } catch {
      /* ignore */
    }

    (async () => {
      // ── Aggregated stats from studentProgress collection ──────────────────
      let firestoreAggLoaded = false;
      if (email) {
        try {
          const snapshot = await getStudentProgressSnapshot(email);
          if (snapshot) {
            setAgg({
              totalVerses: snapshot.totalVerses,
              averageScore: snapshot.averageScore,
              hoursLearned: snapshot.hoursLearned,
              dayStreak: snapshot.dayStreak,
              dailySeries: snapshot.dailySeries || [],
              highScores: [],
              achievements: {
                firstVerse: snapshot.totalVerses > 0,
                perfectScore: false,
                fiveDayStreak: snapshot.dayStreak >= 5,
                tenDayStreak: snapshot.dayStreak >= 10,
                master: snapshot.averageScore >= 95 && snapshot.totalVerses >= 50,
                torahScholar: snapshot.totalVerses >= 100,
              },
            });
            firestoreAggLoaded = true;
          }
        } catch {
          /* fall through */
        }
      }
      if (!firestoreAggLoaded) {
        setAgg(computeAggregates());
      }

      // ── Per-verse data from verseScores collection ────────────────────────
      const currentParasha = getParasha();
      if (userId) {
        try {
          const verseScores = await getVerseScoresByUser(userId);
          setTotalRecordings(verseScores.length);
          setDistinctParashot(new Set(verseScores.map((s) => s.parasha)).size);
          if (currentParasha) {
            const ps = verseScores.filter((s) => s.parasha === currentParasha.english);
            setParashaUnique(new Set(ps.map((s) => s.verseKey)).size);
            if (ps.length > 0) setParashaLastVerse(Math.max(...ps.map((s) => s.verseIndex)) + 1);
          }
          return;
        } catch {
          /* fall through to localStorage */
        }
      }
      // Fallback: localStorage
      const allScores = loadScores();
      setTotalRecordings(allScores.length);
      setDistinctParashot(new Set(allScores.map((s) => s.parasha)).size);
      if (currentParasha) {
        const ps = allScores.filter((s) => s.parasha === currentParasha.english);
        setParashaUnique(new Set(ps.map((s) => s.verseKey)).size);
        if (ps.length > 0) setParashaLastVerse(Math.max(...ps.map((s) => s.verseIndex)) + 1);
      }
    })();
  }, []);

  const countdown = useMemo(() => {
    if (!targetDate) return null;
    const diff = Math.max(0, targetDate.getTime() - Date.now());
    const total = Math.floor(diff / 86400000);
    return { months: Math.floor(total / 30), weeks: Math.floor((total % 30) / 7), days: total % 7 };
  }, [targetDate]);

  // Course progress: unique verses practiced out of the actual parasha verse count
  const coursePct = useMemo(
    () => Math.min(100, Math.round(((agg?.totalVerses || 0) / courseLimit) * 100)),
    [agg, courseLimit],
  );

  const parashaPct = Math.min(100, Math.round((parashaUnique / courseLimit) * 100));

  const accuracyPct = agg?.averageScore ?? 0;
  const hours = agg?.hoursLearned ?? 0;
  const dayStreak = agg?.dayStreak ?? 0;
  const parashaLabel = parasha
    ? isRTL
      ? parasha.hebrew || getParashaByEnglish(parasha.english)?.hebrew || parasha.english
      : parasha.english
    : null;

  const series = agg?.dailySeries || [];
  const CW = 280,
    CH = 80;
  const pts =
    series.length > 1
      ? series
          .map((p, i) => `${(i / (series.length - 1)) * CW},${CH - (p.avg / 100) * CH}`)
          .join(' ')
      : '';
  const area =
    series.length > 1
      ? `M0,${CH} ` +
        series
          .map((p, i) => `L${(i / (series.length - 1)) * CW},${CH - (p.avg / 100) * CH}`)
          .join(' ') +
        ` L${CW},${CH}Z`
      : '';

  const firstName = userName.split(' ')[0] || '';

  // Achievements derived from real data
  const achiev = agg?.achievements;
  const hasFiveParashot = distinctParashot >= 5;
  const has100Verses = achiev?.torahScholar ?? false; // totalVerses >= 100
  const has50Recordings = totalRecordings >= 50;
  const isMaster = achiev?.master ?? false; // avg >= 95 & totalVerses >= 50

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        background: BG,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <UserHeader />
      <div
        style={{
          flexGrow: 1,
          overflowY: 'auto',
          paddingBottom: 90,
          direction: isRTL ? 'rtl' : 'ltr',
        }}
      >
        <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* ── Greeting + countdown card ── */}
          <div
            style={{
              background: 'white',
              borderRadius: 18,
              padding: 16,
              boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
              border: `1px solid ${BORDER}`,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              {/* Avatar — rightmost in RTL */}
              <div
                style={{
                  flexShrink: 0,
                  alignSelf: 'flex-end',
                  width: 72,
                  height: 72,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {userPhotoURL ? (
                  <img
                    src={userPhotoURL}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : (
                  <img
                    src={cheeringKid}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                )}
              </div>

              {/* Greeting + countdown */}
              <div style={{ flex: 1 }}>
                {firstName ? (
                  <div
                    style={{ fontSize: 17, fontWeight: 800, color: TEXT_DARK, marginBottom: 12 }}
                  >
                    {t('my_progress_greeting', { name: firstName })}
                  </div>
                ) : null}
                {countdown ? (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <CountBox
                      value={countdown.days}
                      label={t('my_progress_days')}
                      color={GOLD}
                      textColor={TEXT_DARK}
                      size="small"
                    />
                    <CountBox
                      value={countdown.weeks}
                      label={t('my_progress_weeks')}
                      color={GOLD}
                      textColor={TEXT_DARK}
                      size="small"
                    />
                    <CountBox
                      value={countdown.months}
                      label={t('my_progress_months')}
                      color={GOLD}
                      textColor={TEXT_DARK}
                      size="small"
                    />
                    <div style={{ fontSize: 12, color: TEXT_GRAY, whiteSpace: 'nowrap' }}>
                      {t('my_progress_big_day')}
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: 13, color: TEXT_GRAY }}>{t('my_progress_set_date')}</div>
                )}
              </div>
            </div>
          </div>

          {/* ── Course progress ── */}
          <div
            style={{
              background: 'white',
              borderRadius: 18,
              padding: 16,
              boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
              border: `1px solid ${BORDER}`,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 12,
              }}
            >
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: TEXT_DARK }}>
                  {t('my_progress_course_title')}
                </div>
                <div style={{ fontSize: 12, color: TEXT_GRAY, marginTop: 2 }}>
                  {t('my_progress_verses_of', {
                    current: agg?.totalVerses || 0,
                    total: courseLimit,
                  })}
                </div>
              </div>
              <div style={{ fontSize: 32, fontWeight: 900, color: BLUE }}>{coursePct}%</div>
            </div>
            <div
              style={{ height: 8, background: '#E5E7EB', borderRadius: 999, overflow: 'hidden' }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${coursePct}%`,
                  background: BLUE,
                  borderRadius: 999,
                  transition: 'width 0.6s ease',
                }}
              />
            </div>
          </div>

          {/* ── Weekly parasha ── */}
          {parashaLabel && (
            <div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 10,
                }}
              >
                <div style={{ fontSize: 18, fontWeight: 700, color: TEXT_DARK }}>
                  {t('weekly_portion')}
                </div>
                <button
                  onClick={() => navigate('/choose-parasha')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: BLUE,
                    fontSize: 13,
                    cursor: 'pointer',
                    padding: 0,
                  }}
                >
                  {t('my_progress_all_parashot')}
                </button>
              </div>

              <div
                style={
                  {
                    display: 'flex',
                    overflowX: 'auto',
                    gap: 12,
                    paddingBottom: 4,
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                  } as React.CSSProperties
                }
              >
                {/* Active card */}
                <div
                  style={{
                    background: '#2B4FCC',
                    borderRadius: 18,
                    padding: 16,
                    flexShrink: 0,
                    width: '84%',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div
                        style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginBottom: 4 }}
                      >
                        {t('my_progress_studying_now')}
                      </div>
                      <div style={{ fontSize: 22, fontWeight: 900, color: 'white' }}>
                        {parashaLabel}
                      </div>
                      <div style={{ display: 'flex', gap: 16, marginTop: 6 }}>
                        {parashaLastVerse != null && (
                          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)' }}>
                            {t('my_progress_verse_num', { n: parashaLastVerse })}
                          </div>
                        )}
                        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)' }}>
                          {t('completed_percent', { percent: parashaPct })}
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        width: 44,
                        height: 44,
                        background: 'rgba(255,255,255,0.18)',
                        borderRadius: 12,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 22,
                        flexShrink: 0,
                      }}
                    >
                      📖
                    </div>
                  </div>
                  <div
                    style={{
                      height: 6,
                      background: 'rgba(255,255,255,0.25)',
                      borderRadius: 999,
                      marginTop: 16,
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        height: '100%',
                        width: `${parashaPct}%`,
                        background: GOLD,
                        borderRadius: 999,
                      }}
                    />
                  </div>
                </div>

                {/* Peeking card */}
                <div
                  style={{
                    background: 'white',
                    borderRadius: 18,
                    padding: '14px 16px',
                    border: `1px solid ${BORDER}`,
                    flexShrink: 0,
                    width: '72%',
                    opacity: 0.7,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div>
                    <div style={{ fontSize: 11, color: TEXT_GRAY, marginBottom: 3 }}>
                      {t('my_progress_next_up')}
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: TEXT_DARK }}>
                      {t('my_progress_another_parasha')}
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: TEXT_GRAY }}>
                    {t('my_progress_not_started')}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Reading accuracy ── */}
          <div
            style={{
              background: 'white',
              borderRadius: 18,
              padding: 16,
              boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
              border: `1px solid ${BORDER}`,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                marginBottom: 12,
              }}
            >
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: TEXT_DARK }}>
                  {t('my_progress_accuracy_title')}
                </div>
                <div style={{ fontSize: 12, color: TEXT_GRAY, marginTop: 2 }}>
                  {t('my_progress_last_7_days')}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <span style={{ fontSize: 13, color: '#22C55E', fontWeight: 700 }}>↗</span>
                <span
                  style={{
                    fontSize: 32,
                    fontWeight: 900,
                    color: accuracyPct > 0 ? '#22C55E' : TEXT_GRAY,
                    lineHeight: 1,
                  }}
                >
                  {accuracyPct}%
                </span>
              </div>
            </div>

            {series.some((p) => p.avg > 0) ? (
              <>
                <svg
                  viewBox={`0 0 ${CW} ${CH}`}
                  style={{ width: '100%', height: 80, direction: 'ltr' }}
                >
                  {area && <path d={area} fill="rgba(59,123,244,0.1)" />}
                  {pts && (
                    <polyline
                      points={pts}
                      fill="none"
                      stroke={BLUE}
                      strokeWidth={2.5}
                      strokeLinejoin="round"
                      strokeLinecap="round"
                    />
                  )}
                  {series.map(
                    (p, i) =>
                      p.avg > 0 && (
                        <circle
                          key={i}
                          cx={(i / Math.max(series.length - 1, 1)) * CW}
                          cy={CH - (p.avg / 100) * CH}
                          r={3.5}
                          fill="white"
                          stroke={BLUE}
                          strokeWidth={2}
                        />
                      ),
                  )}
                  {series.length > 0 && series[series.length - 1].avg > 0 && (
                    <circle
                      cx={CW}
                      cy={CH - (series[series.length - 1].avg / 100) * CH}
                      r={5}
                      fill={BLUE}
                    />
                  )}
                </svg>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: 4,
                    direction: 'ltr',
                  }}
                >
                  {series.map((p, i) => (
                    <span key={i} style={{ fontSize: 11, color: TEXT_GRAY }}>
                      {HEBREW_DAYS[new Date(p.date).getDay()]}
                    </span>
                  ))}
                </div>
              </>
            ) : (
              <div
                style={{ textAlign: 'center', padding: '20px 0', color: TEXT_GRAY, fontSize: 13 }}
              >
                {t('my_progress_no_data')}
              </div>
            )}
          </div>

          {/* ── Achievements ── */}
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: TEXT_DARK, marginBottom: 10 }}>
              {t('my_progress_achievements_title')}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <AchievementTile
                icon={<span style={{ fontSize: 24 }}>🏆</span>}
                iconBg="rgba(245,200,66,0.15)"
                title={t('my_progress_parashot_count', { count: distinctParashot })}
                subtitle={t('my_progress_completed_successfully')}
                active={hasFiveParashot}
              />
              <AchievementTile
                icon={<CircleIcon color={BLUE} />}
                iconBg="rgba(59,123,244,0.1)"
                title={t('my_progress_verses_count', { count: agg?.totalVerses || 0 })}
                subtitle={t('my_progress_recorded_verses')}
                active={has100Verses}
              />
              <AchievementTile
                icon={<MicIcon color="#A855F7" />}
                iconBg="rgba(168,85,247,0.1)"
                title={t('my_progress_consistent_recorder')}
                subtitle={t('my_progress_recordings_count', { count: totalRecordings })}
                active={has50Recordings}
              />
              <AchievementTile
                icon={<span style={{ fontSize: 24 }}>👑</span>}
                iconBg="rgba(245,200,66,0.15)"
                title={t('my_progress_reading_master')}
                subtitle={t('my_progress_avg_score_pct', { pct: accuracyPct })}
                active={isMaster}
              />
            </div>
          </div>

          {/* ── Practice summary ── */}
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: TEXT_DARK, marginBottom: 10 }}>
              {t('my_progress_summary_title')}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              <SummaryTile
                icon={<ClockIcon color="rgba(255,255,255,0.85)" />}
                value={hours}
                label={t('my_progress_study_hours')}
                bg={BLUE}
                textColor="white"
                subColor="rgba(255,255,255,0.75)"
              />
              <SummaryTile
                icon={<BarChartIcon color={TEXT_GRAY} />}
                value={totalRecordings}
                label={t('recordings')}
                bg="white"
                textColor={TEXT_DARK}
                subColor={TEXT_GRAY}
                border={BORDER}
              />
              <SummaryTile
                icon={<span style={{ fontSize: 20 }}>🔥</span>}
                value={dayStreak}
                label={t('day_streak')}
                bg="white"
                textColor={TEXT_DARK}
                subColor={TEXT_GRAY}
                border={BORDER}
              />
            </div>
          </div>
        </div>
      </div>
      <BottomNav />
    </div>
  );
};

// ── Sub-components ───────────────────────────────────────────────────────────

const COUNT_BOX_SIZES = {
  small: { width: 42, height: 46, numSize: 16, labelSize: 8, borderRadius: 8 },
  medium: { width: 54, height: 60, numSize: 22, labelSize: 10, borderRadius: 10 },
  big: { width: 68, height: 76, numSize: 30, labelSize: 12, borderRadius: 14 },
};

const CountBox: React.FC<{
  value: number;
  label: string;
  color: string;
  textColor: string;
  size?: 'small' | 'medium' | 'big';
}> = ({ value, label, color, textColor, size = 'medium' }) => {
  const s = COUNT_BOX_SIZES[size];
  return (
    <div
      style={{
        background: color,
        color: textColor,
        borderRadius: s.borderRadius,
        width: s.width,
        height: s.height,
        fontWeight: 800,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
      }}
    >
      <div style={{ fontSize: s.numSize, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: s.labelSize, fontWeight: 600, opacity: 0.75 }}>{label}</div>
    </div>
  );
};

const SummaryTile: React.FC<{
  icon: React.ReactNode;
  value: number | string;
  label: string;
  bg: string;
  textColor: string;
  subColor: string;
  border?: string;
}> = ({ icon, value, label, bg, textColor, subColor, border }) => (
  <div
    style={{
      background: bg,
      borderRadius: 16,
      padding: '14px 8px',
      border: border ? `1px solid ${border}` : undefined,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 4,
    }}
  >
    {icon}
    <div style={{ fontSize: 26, fontWeight: 900, color: textColor, lineHeight: 1 }}>{value}</div>
    <div style={{ fontSize: 11, color: subColor, textAlign: 'center' }}>{label}</div>
  </div>
);

const AchievementTile: React.FC<{
  icon: React.ReactNode;
  iconBg: string;
  title: string;
  subtitle: string;
  active: boolean;
}> = ({ icon, iconBg, title, subtitle, active }) => (
  <div
    style={{
      background: active ? 'white' : '#F3F4F6',
      borderRadius: 16,
      padding: '16px 12px',
      border: `1px solid ${active ? BORDER : '#E5E7EB'}`,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 8,
      textAlign: 'center',
      opacity: active ? 1 : 0.5,
    }}
  >
    <div
      style={{
        width: 46,
        height: 46,
        borderRadius: '50%',
        background: active ? iconBg : '#E5E7EB',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {icon}
    </div>
    <div style={{ fontSize: 15, fontWeight: 800, color: active ? TEXT_DARK : TEXT_GRAY }}>
      {title}
    </div>
    <div style={{ fontSize: 11, color: TEXT_GRAY }}>{subtitle}</div>
  </div>
);

const CircleIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 3" strokeLinecap="round" />
  </svg>
);
const MicIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <rect x="9" y="2" width="6" height="12" rx="3" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v3M8 22h8" strokeLinecap="round" />
  </svg>
);
const BarChartIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <rect x="3" y="6" width="3" height="14" rx="1" />
    <rect x="10" y="10" width="3" height="10" rx="1" />
    <rect x="17" y="3" width="3" height="17" rx="1" />
  </svg>
);
const ClockIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v6l4 2" strokeLinecap="round" />
  </svg>
);

export default MyProgressPage;
